import { g as getFocusableElements } from './index-D9jOD0sH.js';
import { t as throttle } from './throttle-DcOCiHmQ.js';
import './debounce-CFtxQv9K.js';

const trapFocusHandlers = {};

function trapFocus({ container, elementToFocus = container }) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event?.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();
}

/**
 * @param {HTMLElement} elementToFocus
 */
function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

class CollapsibleHeader extends HTMLElement {
  menuItemControllers = [...this.querySelectorAll('[aria-controls]:not([data-submenu-close])')];
  closeButtonControllers = [...this.querySelectorAll('[data-submenu-close]')];

  backdrop = document.querySelector('sticky-header [data-backdrop]');

  connectedCallback() {
    this.menuItemControllers.forEach((menuItemController) => {
      menuItemController.addEventListener('click', () => this.toggle(menuItemController));
    });

    this.closeButtonControllers.forEach((closeButtonController) => {
      closeButtonController.addEventListener('click', () => {
        const parentMenuItem = document.querySelector(
          `[aria-controls="${closeButtonController.getAttribute('aria-controls')}"]`
        );
        parentMenuItem.ariaExpanded = 'false';
        this.toggle(closeButtonController);
      });
    });

    this.backdrop.addEventListener('click', () => this.closeAll());
  }

  handleBodyClass() {
    const hasExpanded = this.querySelector('[aria-expanded="true"]');
    document.body.classList.toggle('header-is-open', hasExpanded);
  }

  toggle(controller) {
    const submenu = document.querySelector(`#${controller.getAttribute('aria-controls')}`);
    const closeButton = submenu.querySelector('[data-submenu-close]');
    const isExpanded = controller.ariaExpanded === 'true';
    isExpanded
      ? this.close(controller, submenu, closeButton)
      : this.open(controller, submenu, closeButton);

    this.handleBodyClass();
  }

  close(controller, submenu, closeButton) {
    this.closeChildren(controller);
    controller.ariaExpanded = 'false';
    submenu.ariaHidden = 'true';
    closeButton.ariaExpanded = 'false';
  }

  open(controller, submenu, closeButton) {
    this.closeSiblings(controller);
    controller.ariaExpanded = 'true';
    submenu.ariaHidden = 'false';
    closeButton.ariaExpanded = 'true';
  }

  closeChildren(controller) {
    const currentLevel = +controller.dataset.linkLevel;

    const siblings = this.menuItemControllers.filter(
      (controller) => +controller.dataset.linkLevel > currentLevel
    );

    siblings.forEach((sibling) => {
      const submenu = document.querySelector(`#${sibling.getAttribute('aria-controls')}`);
      const closeButton = submenu.querySelector('[data-submenu-close]');
      this.close(sibling, submenu, closeButton);
    });
  }

  closeSiblings(controller) {
    const currentLevel = +controller.dataset.linkLevel;

    const siblings = this.menuItemControllers.filter(
      (controller) => +controller.dataset.linkLevel === currentLevel
    );

    siblings.forEach((sibling) => {
      const submenu = document.querySelector(`#${sibling.getAttribute('aria-controls')}`);
      const closeButton = submenu.querySelector('[data-submenu-close]');
      this.close(sibling, submenu, closeButton);
    });
  }

  closeAll() {
    this.menuItemControllers.forEach((controller) => {
      const submenu = document.querySelector(`#${controller.getAttribute('aria-controls')}`);
      const closeButton = submenu.querySelector('[data-submenu-close]');
      this.close(controller, submenu, closeButton);
    });

    this.handleBodyClass();
  }
}

class ToggleMobileNavigation extends ToggleElementClass {
  constructor() {
    super();

    this.header = this.closest('[data-header]');

    if (this.header) {
      this.shadows = this.header.querySelectorAll('.header-shadow');
      this.inverse = !!this.header.classList.contains('header--inverse');

      // Extra events
      document.addEventListener('keyup', (event) => this.onKeyUp(event));

      this.shadows?.forEach((shadow) => {
        shadow.addEventListener('click', (event) => this.remove(event));
      });
    }
  }

  events() {
    if (!this.header) super.events();
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('menu-drawer-is-open')) return;
    if (event?.code.toUpperCase() !== 'ESCAPE') return;

    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) this.remove();
  }

  add() {
    super.add();
    trapFocus(this.header);
  }

  remove() {
    super.remove();
    this.closeAllSiblings();
    removeTrapFocus();
  }

  closeAllSiblings() {
    const collapsibles = this.target.querySelector('collapsible-element');
    collapsibles.closeAll();
  }
}

class ToggleSearch extends ToggleElementClass {
  constructor() {
    super();
  }

  construct() {
    super.construct();

    // elements
    this.header = this.closest('[data-header]');

    // options
    const { toggleClass, target } = this.options;

    // key up escape
    document.addEventListener('keyup', (event) => {
      this.onKeyUp(event);
    });

    // Remove classes in case the header navigation drawer is open.
    this.addEventListener('click', () => {
      document.body.classList.remove('header-is-open');
      document.body.classList.remove('menu-drawer-is-open');
      // Focus on input element if provided
      if (this.options.focusTarget) {
        document.querySelector(this.options.focusTarget)?.focus();
      }
    });
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('header-search-is-open')) return;
    if (event?.code.toUpperCase() !== 'ESCAPE') return;
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) this.remove();
  }

  add() {
    super.add();
    if (this.header) document.body.classList.add('header-search-is-open');
    if (this.target) trapFocus(this.target);
  }

  remove() {
    super.remove();
    if (this.header) document.body.classList.remove('header-search-is-open');
    removeTrapFocus();
  }
}

class StickyHeader extends HTMLElement {
  constructor() {
    super();

    // select element
    this.headerElement = this;
    this.header = document.querySelector('[data-header]');
    this.headerWrapper = document.querySelector('.shopify-section-group-header-group');
    this.announcementBar = document.querySelector('.announcement-bar');
    this.headerInverse = !!this.headerElement.classList.contains('header--inverse');
    this.headerNav = this.querySelector('collapsible-header');
    this.mobileNav = this.querySelector('toggle-mobile-navigation');
    this.search = this.querySelector('[data-header-search]');
    this.searchToggle = this.querySelector('toggle-search');
    this.productInfo = document.querySelector('[data-product-info]');

    if (this.headerWrapper) {
      // scroll parameters
      this.currentScrollTop = 0;
      this.scrollOffset = 0;
      this.headerStart = 0;

      // scroll event
      this.raf = null; // Safe request animation frame
      ((this.timer = null), // Safe scroll timer
        window.addEventListener(
          'scroll',
          () => {
            clearTimeout(this.timer);
            if (!this.raf) {
              // A request animation frame is used to animate on max 60fps.
              this.raf = requestAnimationFrame(this.scrollAnimation.bind(this));
            }
            this.timer = setTimeout(() => {
              // Stop animation, cancel request animation frame
              cancelAnimationFrame(this.raf);
              this.raf = null;
            }, 50);
          },
          false
        ));
    }
  }

  connectedCallback() {
    this.calculateHeaderOffset();

    ['resize', 'scroll'].forEach((event) => {
      window.addEventListener(
        event,
        throttle(() => this.calculateHeaderOffset(), 100)
      );
    });
  }

  calculateHeaderOffset() {
    this.scrollOffset = 0;
    this.headerStart = 0;

    if (this.announcementBar) {
      const announcementBarHeight = this.announcementBar.offsetHeight;
      const announcementBarRect = this.announcementBar.getBoundingClientRect();
      const visibleHeight = Math.max(
        0,
        Math.min(announcementBarHeight, announcementBarHeight + announcementBarRect.top)
      );
      this.scrollOffset += visibleHeight;
      this.headerStart += visibleHeight;
    }

    if (this.headerElement) this.scrollOffset += this.headerElement.offsetHeight;

    document.documentElement.style.setProperty('--header-height', `${this.scrollOffset}px`);
  }

  scrollAnimation() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    // only animate if header is closed.
    if (!document.body.classList.contains('header-is-open')) {
      if (scrollTop + window.innerHeight > document.body.clientHeight) {
        this.hide();
      } else if (scrollTop > this.currentScrollTop && scrollTop > this.scrollOffset) {
        this.hide();
      } else if (scrollTop <= this.headerStart) {
        this.revealTop();
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerStart) {
        this.reveal();
      }
    }

    this.currentScrollTop = scrollTop;

    // Recall request animation loop
    this.raf = requestAnimationFrame(this.scrollAnimation.bind(this));
  }

  hide() {
    document.body.classList.add('header-hidden', 'header-sticky');
    this.closeHeaderNav();
    this.closeMobileNav();
    this.closeSearch();
    this.productInfoPosition('up');
  }

  reveal() {
    document.body.classList.add('header-sticky');
    document.body.classList.remove('header-hidden');
    if (this.headerInverse) document.body.classList.add('header-inverse-solid');
    this.productInfoPosition('down');
  }

  revealTop() {
    if (this.headerInverse && !document.body.classList.contains('header-is-open')) {
      document.body.classList.remove('header-inverse-solid');
    }
    document.body.classList.remove('header-hidden', 'header-sticky');
    this.productInfoPosition('up');
  }

  closeHeaderNav() {
    if (!this.headerNav || window.innerWidth < 768) return false;
    this.headerNav.closeAll();
  }

  closeMobileNav() {
    if (!this.mobileNav) return false;
    this.mobileNav.remove();
  }

  closeSearch() {
    if (!this.search || !this.searchToggle) return false;
    if (this.search.classList.contains('header__search--is-active')) {
      this.searchToggle.remove();
    }
  }

  productInfoPosition(pos = 'up') {
    if (!this.productInfo) return;
    if (pos == 'down') {
      this.productInfo.classList.replace('top-0', 'top-[4.5rem]');
    } else {
      this.productInfo.classList.replace('top-[4.5rem]', 'top-0');
    }
  }
}

if (!customElements.get('collapsible-header')) {
  customElements.define('collapsible-header', CollapsibleHeader);
}

if (!customElements.get('toggle-mobile-navigation')) {
  customElements.define('toggle-mobile-navigation', ToggleMobileNavigation);
}

if (!customElements.get('toggle-search')) {
  customElements.define('toggle-search', ToggleSearch);
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}
